import { useMemo } from "react";
import dayjs from "dayjs";
import { parseDataKey } from "../utils/dateRange";
import { aggregateMinutes } from "../utils/aggregateMinutes";
import { shiftedDay } from "../utils/timezoneShift";
import { shiftMinutesFor } from "../utils/avgActiveTimezone";

/* =========================
   HELPERS
========================= */

function uiDateToDataDay(uiDate) {
  if (!uiDate) return null;
  const [y, m, d] = uiDate.split("-");
  const dt = dayjs(`${d}-${m}-${y}`, "DD-MM-YYYY", true);
  return dt.isValid() ? dt : null;
}

function getWeekStartMonday(d) {
  const offset = (d.day() + 6) % 7;
  return d.subtract(offset, "day").startOf("day");
}

// One day of minute data on the selected timezone's clock. Without a shift (or
// for Turkey itself) it is the stored day untouched.
function dayInTimezone(rawVideo, uiKey, timezone, sourceOffset) {
  const curr = rawVideo[uiKey] || [];
  if (!timezone) return curr;
  const [d, m, y] = uiKey.split("-");
  const ymd = `${y}-${m}-${d}`;
  const shift = shiftMinutesFor(timezone, ymd, sourceOffset);
  if (!shift) return curr;
  const key = (offset) =>
    dayjs(ymd).add(offset, "day").format("DD-MM-YYYY");
  return shiftedDay(rawVideo[key(-1)], curr, rawVideo[key(1)], shift);
}

function minuteToLabel(i) {
  const h = String(Math.floor(i / 60)).padStart(2, "0");
  const m = String(i % 60).padStart(2, "0");
  return `${h}:${m}`;
}

function timeToMinute(t) {
  if (!t || typeof t !== "string") return null;
  const [h, m] = t.split(":").map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
}

/* =========================
   MAIN HOOK
========================= */

export function useAvgActiveSeries({
  rawVideo,
  sortedVideoKeys,
  startDateInput,
  endDateInput,
  startTime,
  endTime,
  mode,
  selectedWeekdays,
  timezone,
  sourceOffset = 3,
  useTimeWindow = false,
}) {
  return useMemo(() => {
    if (!rawVideo || !sortedVideoKeys?.length) return [];

    let winStart = 0;
    let winEnd = 1439;
    if (useTimeWindow) {
      const s = timeToMinute(startTime);
      const e = timeToMinute(endTime);
      if (Number.isFinite(s)) winStart = Math.max(0, Math.min(1439, s));
      if (Number.isFinite(e)) winEnd = Math.max(0, Math.min(1439, e));
      if (winEnd < winStart) [winStart, winEnd] = [winEnd, winStart];
    }
    const winLength = winEnd - winStart + 1;
    const daySum = (arr) => {
      let total = 0;
      for (let i = winStart; i <= winEnd; i++) total += Number(arr[i] || 0);
      return total;
    };

    /* =========================
       MINUTE  ✅
    ========================= */

    if (mode === "minute") {
      const uiKey = startDateInput
        ? startDateInput.split("-").reverse().join("-")
        : sortedVideoKeys.at(-1);

      const arr = dayInTimezone(rawVideo, uiKey, timezone, sourceOffset);

      let startMin = timeToMinute(startTime);
      let endMin = timeToMinute(endTime);

      if (!Number.isFinite(startMin)) startMin = 0;
      if (!Number.isFinite(endMin)) endMin = 1439;

      startMin = Math.max(0, Math.min(1439, startMin));
      endMin = Math.max(0, Math.min(1439, endMin));

      if (endMin < startMin) [startMin, endMin] = [endMin, startMin];

      const out = [];
      for (let i = startMin; i <= endMin; i++) {
        out.push({
          index: i,
          label: minuteToLabel(i),
          value: Math.round((Number(arr[i]) || 0) / 2),
        });
      }

      return out;
    }

    /* =========================
       EPG  ✅
    ========================= */

    if (mode === "epg") {
      const uiKey = startDateInput
        ? startDateInput.split("-").reverse().join("-")
        : sortedVideoKeys.at(-1);

      const arr = dayInTimezone(rawVideo, uiKey, timezone, sourceOffset);

      let startMin = timeToMinute(startTime);
      let endMin = timeToMinute(endTime);

      if (!Number.isFinite(startMin)) startMin = 0;
      if (!Number.isFinite(endMin)) endMin = 1439;

      startMin = Math.max(0, Math.min(1439, startMin));
      endMin = Math.max(0, Math.min(1439, endMin));

      if (endMin < startMin) [startMin, endMin] = [endMin, startMin];

      const out = [];
      for (let i = startMin; i <= endMin; i++) {
        out.push({
          index: i,
          label: minuteToLabel(i),
          value: Math.round((Number(arr[i]) || 0) / 2),
        });
      }

      return out;
    }

    /* =========================
       DATE RANGE
    ========================= */

    const start = uiDateToDataDay(startDateInput);
    const end = uiDateToDataDay(endDateInput);
    if (!start || !end) return [];

    /* =========================
       ⭐ CUSTOM (SEGMENT ONLY)
    ========================= */

    if (mode === "custom") {
      if (!selectedWeekdays || selectedWeekdays.length === 0) return [];

      return sortedVideoKeys
        .map((key) => {
          const d = parseDataKey(key);
          if (!d) return null;
          if (d.isBefore(start, "day") || d.isAfter(end, "day")) return null;

          const weekdayName = d.format("dddd");
          if (!selectedWeekdays.includes(weekdayName)) return null;

          const arr = rawVideo[key] || [];
          const sum = daySum(arr);

          return {
            label: d.format("DD.MM"),
            value: Math.round(sum / winLength / 2),
            weekday: weekdayName,
          };
        })
        .filter(Boolean);
    }

    /* =========================
       HOUR
    ========================= */

    if (mode === "hour") {
      const out = [];

      sortedVideoKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const arr = rawVideo[key] || [];
        const hours = aggregateMinutes(arr, "hour");

        hours.forEach((v, h) => {
          out.push({
            label: `${d.format("DD.MM")} ${String(h).padStart(2, "0")}:00`,
            value: Math.round((Number(v) || 0) / 60 / 2),
          });
        });
      });

      return out;
    }

    /* =========================
       DAY
    ========================= */

    if (mode === "day") {
      return sortedVideoKeys
        .map((key) => {
          const d = parseDataKey(key);
          if (!d) return null;
          if (d.isBefore(start, "day") || d.isAfter(end, "day")) return null;

          const arr = rawVideo[key] || [];
          const sum = daySum(arr);

          return {
            label: d.format("DD.MM"),
            value: Math.round(sum / winLength / 2),
            fullDate: d.format("YYYY-MM-DD"),
          };
        })
        .filter(Boolean);
    }

    /* =========================
       WEEK
    ========================= */

    if (mode === "week") {
      const bucket = new Map();

      sortedVideoKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const weekStart = getWeekStartMonday(d);
        const bucketKey = weekStart.format("YYYY-MM-DD");

        const arr = rawVideo[key] || [];
        const sum = daySum(arr);

        bucket.set(bucketKey, (bucket.get(bucketKey) || 0) + sum);
      });

      return Array.from(bucket.entries()).map(([key, sum]) => ({
        label: dayjs(key).format("DD.MM"),
        value: Math.round(sum / (7 * winLength) / 2),
      }));
    }

    /* =========================
       MONTH
    ========================= */

    if (mode === "month") {
      const bucket = new Map();

      sortedVideoKeys.forEach((key) => {
        const d = parseDataKey(key);
        if (!d) return;
        if (d.isBefore(start, "day") || d.isAfter(end, "day")) return;

        const bucketKey = d.format("YYYY-MM");

        const arr = rawVideo[key] || [];
        const sum = daySum(arr);

        bucket.set(bucketKey, (bucket.get(bucketKey) || 0) + sum);
      });

      return Array.from(bucket.entries()).map(([key, sum]) => ({
        label: dayjs(key).format("MM.YYYY"),
        value: Math.round(sum / (30 * winLength) / 2),
      }));
    }

    return [];
  }, [
    rawVideo,
    sortedVideoKeys,
    startDateInput,
    endDateInput,
    startTime,
    endTime,
    mode,
    selectedWeekdays,
    timezone,
    sourceOffset,
    useTimeWindow,
  ]);
}
